import React, { useState, useEffect, useRef, useMemo } from 'react';
import ClockReport from './Clock.component';
import ClockReportMobile from './ClockMobile.component';
import { modalService } from '../../../services/modal.service';
import CompleteEntryContainer from './CompleteEntryModal/CompleteEntryModal.container';
import AddRequestBottomSheet from '../../../modals/AddRequestModal/AddRequestBottomSheet.component';
import CompleteEntryBottomSheet from './CompleteEntryModal/CompleteEntryBottomSheet.component';
import EventsAndProjectsSelectComponent from './EventsAndProjectsSelect/EventsAndProjectsSelect.component';
import httpService from '../../../services/http.service';
import {
	convertFromUtc,
	calcTimerInitValue,
	cantReportModal,
	onDelete,
} from './Clock.utils';
import { Record } from './Record/Record.component';
import { openAlertModal } from './Clock.utils';
import useDevicePlatform, {
	DeviceType,
} from '../../../hooks/useDevicePlatform.hook';
import { i18nService } from '../../../services/i18n.service';
import I18n from '../i18n/I18n.component';
import moment from 'moment';
import { Option } from './EventsAndProjectsSelect/EventsAndProjectsSelect.utils';
import { toNumber } from 'lodash';
import { getUserData } from '../../../store/selectors/login.selectors';
import { Privileges } from '../../../constants/roleTypes.constant';
import { useSelector, useDispatch } from 'react-redux';
import {
	getTodayWorkLogs,
	getReFetchedFlag,
} from '../../../store/selectors/clockReport.selector';
import {
	setTodayWorklogs,
	toggleReFetch,
} from '../../../store/actions/clockReport.actions';
import { useRoute } from '@react-navigation/native';
import AddRequest from '../../../modals/AddRequestModal/AddRequestModal.container';
import { getCurrentDateTime } from '../../../store/actions/dateTime.actions';
import { getServerDateTime } from '../../../store/selectors/dateTime.selectors';
import { getState } from '../../../store/store';
import { formatStandardHours } from '../../../modals/AddRequestModal/AddRequestModal.utils';
import { getLocationPermission } from '../../../utils/permissions.utils';
import { AppState, Platform, View, TouchableOpacity, Pressable, Text } from 'react-native';
import ImageIcon from '../Icon/ImageIcon.component';
import useEffectNotInitial from '../../../hooks/useEffectNotInitial';
import { snackbarService } from '../../../services/snackbar.service';
import ActionButtonContainer from '../Buttons/ActionButton.container';
import colors from '../../../config/colors';

interface SelectedItem {
	name: any;
	isProject: boolean;
	id: any;
	isFullDay: boolean;
}

// currentDateTime holds the server's UTC time - convert it before taking the date,
// otherwise between 00:00 and 03:00 Israel time we get yesterday's date
const getCurrentLocalDate = (format = 'YYYY-MM-DD') =>
	convertFromUtc(
		getState().dateTime.currentDateTime,
		false,
		true,
		undefined,
		format,
	);

const ClockReportContainer = ({
	report101warning,
	eventsOptionsProp = null,
	standardHoursProp = undefined,
	workLogListForClock = null,
	compact,
	splitCards,
}: {
	report101warning?: boolean;
	eventsOptionsProp?: any;
	standardHoursProp?: any;
	workLogListForClock?: any;
	compact?: boolean;
	splitCards?: boolean;
}) => {
	const userData = useSelector(getUserData);
	const isFinishWorkDateExpired = useMemo(
		() =>
			moment
				.utc(getState().dateTime.currentDateTime)
				.local()
				.isAfter(
					moment(
						userData?.operative_module?.[101]?.finish_work,
						'YYYY-MM-DD',
					),
				),
		[userData],
	);

	const route = useRoute();
	const dispatch = useDispatch();
	const currentUtcTime = useSelector(getServerDateTime);
	const reFetchFlag = useSelector(getReFetchedFlag);
	const todayWorkLogs = useSelector(getTodayWorkLogs);
	const platform = useDevicePlatform();
	const isMobile = platform !== DeviceType.WEB;
	const isWebOrMobileweb = platform !== DeviceType.MOBILE;
	const Component = useMemo(
		() => (isMobile ? ClockReportMobile : ClockReport),
		[isMobile],
	);
	const isFirstInit = useRef(true);
	const reportedExit = useRef(false);
	const [completeEntrySheetProps, setCompleteEntrySheetProps] = useState<any>(null);
	const [addRequestSheetProps, setAddRequestSheetProps] = useState<any>(null);
	const [initTime, setInitTime] = useState<any>(null);
	const [currentRunnigWorkLog, setCurrentRunningWorkLog] =
		useState<any>(null);
	const [shiftTime, setShiftTime] = useState(12); //default

	const currentRunnigWorkLogRef = useRef<any>();
	currentRunnigWorkLogRef.current = currentRunnigWorkLog;
	const shiftTimeRef = useRef<any>();
	shiftTimeRef.current = shiftTime;
	const canEdit = useMemo(() => {
		return (
			userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_WRITE,
			) && userData.customer_setting.allowUpdateReport
		);
	}, [userData]);
	const [missingExitWorkLog, setMissingExitWorkLog] = useState({
		isMissing: false,
		workLogId: null,
	});
	const [defaultProjectOption, setDefaultProjectOption] = useState({
		name: '',
		id: null,
		lastReported: true,
	});
	const [defaultEventOption, setDefaultEventOption] = useState({
		name: '',
		id: '',
	});
	const [eventsOptions, setEventsOptions] = useState<Option[]>([]);
	const [projectsOptions, setProjectsOptions] = useState<Option[]>([]);
	const [selectedItem, setSelectedItem] = useState<SelectedItem>({
		name: null,
		isProject: true,
		id: null,
		isFullDay: true,
	});
	const [standardHours, setStandartHours] = useState<any>(null);
	const [selectedItemType, setSelectedItemType] = useState<
		'projects' | 'events' | null
	>(null);
	const handleOpenOptions = (
		isOpen: boolean,
		type: 'projects' | 'events',
	) => {
		if (isOpen) {
			switch (type) {
				case 'projects':
					setSelectedItemType('projects');
					break;
				case 'events':
					setSelectedItemType('events');
					break;

				default:
					return;
			}
		} else {
			setSelectedItemType(null);
		}
	};

	useEffect(() => {
		if (!currentRunnigWorkLog && selectedItem?.id) {
			setSelectedItem({
				name: null,
				isProject: true,
				id: null,
				isFullDay: true,
			});
		}
	}, [currentRunnigWorkLog]);

	const canReportFullDay = useMemo(() => {
		if (!standardHours) return todayWorkLogs.data.length === 0;
		//can report full day only if the standard is not bigger than max shift hours and no current work logs
		const standard = formatStandardHours(standardHours);
		const enter = moment(standard.enterTime, 'HH:mm');
		const exit = moment(standard.exitTime, 'HH:mm');

		const canReport = !(
			exit.diff(enter, 'minutes') >
			userData?.customer_setting.maxShiftHour * 60
		);
		return todayWorkLogs.data.length === 0 && canReport;
	}, [todayWorkLogs.data, standardHours]);

	const totalReported = useMemo(
		() =>
			todayWorkLogs.data.reduce((total: number, worklog: Record) => {
				return total + worklog.total / 60;
			}, 0),
		[todayWorkLogs, reFetchFlag],
	);

	// A work log carried over from yesterday is limited by yesterday's reported hours
	const activeDayTotalReported =
		todayWorkLogs.activeWorkLogDayTotalReported ?? totalReported;

	const activeDayTotalReportedRef = useRef<any>();
	activeDayTotalReportedRef.current = activeDayTotalReported;

	const getProjects = async () => {
		try {
			const res = await httpService.api<any>({
				type: 'getAllProjectNameList',
				query: {
					pageName: 'employeeHome',
					date: moment().format('YYYY-MM-DD'),
				},
			});
			if (res) {
				let tempDefaultOption: Option = {
					name: '',
					id: null,
					lastReported: true,
				};
				const options = res.filter((option: Option) => {
					if (option.lastReported) {
						tempDefaultOption = { ...option };
					}
					return option.lastReported === false;
				});
				if (res.length) {
					const allProjectOptions = tempDefaultOption.id
						? [{ ...tempDefaultOption }].concat(options)
						: options;
					setProjectsOptions(allProjectOptions);
					setDefaultProjectOption(tempDefaultOption);
				} else {
					setProjectsOptions([]);
				}
			}
		} catch (e) {}
	};

	const getEvents = async () => {
		try {
			const res = await httpService.api<any>({
				type: 'getAllEventNameList',
				query: { date: moment().format('YYYY-MM-DD') },
			});
			if (res) {
				setEventsOptions(res);
			}
		} catch (e) {}
	};

	useEffect(() => {
		if (!isFirstInit.current) {
			let missingExitWorklog = { isMissing: false, workLogId: null };

			todayWorkLogs.data.forEach((workLog: Record) => {
				if (!workLog.exitTime) {
					missingExitWorklog.isMissing = true;
					missingExitWorklog.workLogId = workLog.workLogId;
				}
			});
			setMissingExitWorkLog(missingExitWorklog);
			setCurrentRunningWorkLog(todayWorkLogs.activeWorkLog);
			if (todayWorkLogs.activeWorkLog?.eventId) {
				setDefaultEventOption({
					id: todayWorkLogs.activeWorkLog.eventId,
					name: '',
				});
			}
			if (!todayWorkLogs.activeWorkLog) {
				setInitTime(null);
			}
		}
	}, [todayWorkLogs]);

	useEffect(() => {
		if (!isFirstInit.current && selectedItem.id && !reportedExit.current) {
			startWorkLog();
		}
		if (reportedExit.current) {
			reportedExit.current = false;
		}
	}, [selectedItem]);

	useEffect(() => {
		if (!isFirstInit.current && currentRunnigWorkLog) {
			const value = calcTimerInitValue(
				currentRunnigWorkLog,
				shiftTime - activeDayTotalReported,
			);
			setInitTime(value);
		} else if (!isFirstInit.current && !currentRunnigWorkLog && initTime) {
			setInitTime(undefined);
		}
	}, [currentRunnigWorkLog]);

	useEffect(() => {
		const subscription: any = AppState.addEventListener(
			'change',
			nextAppState => {
				if (
					Platform.OS !== 'web' &&
					currentRunnigWorkLogRef.current &&
					nextAppState === 'active'
				) {
					const value = calcTimerInitValue(
						currentRunnigWorkLogRef.current,
						shiftTimeRef.current - activeDayTotalReportedRef.current,
					);
					setInitTime(value);
				}
			},
		);

		return () => {
			subscription?.remove();
		};
	}, []);

	const getStandardHours = async () => {
		try {
			const res = await httpService.api<any>({
				type: 'getStandardByDate',
				params: {
					date: getCurrentLocalDate() || moment().format('YYYY-MM-DD'),
				},
			});
			if (res) {
				setStandartHours(res);
			}
		} catch (e) {}
	};

	useEffect(() => {
		if (
			userData?.id &&
			!isFinishWorkDateExpired &&
			(userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_READ,
			) ||
				userData?.employeeRolePrivileges?.includes(
					Privileges.ROLE_LOG_WORK_WRITE,
				))
		) {
			getProjects();
			if (eventsOptionsProp?.length > 0) {
				setEventsOptions(eventsOptionsProp);
			} else {
				getEvents();
			}
			if (standardHoursProp !== undefined) {
				setStandartHours(standardHoursProp);
			} else {
				getStandardHours();
			}
			setInitTime(undefined);
			setDefaultProjectOption({
				name: '',
				id: null,
				lastReported: true,
			});
			setDefaultEventOption({
				name: '',
				id: '',
			});
			setSelectedItem({
				name: null,
				isProject: true,
				id: null,
				isFullDay: true,
			});
		}
	}, [userData]);

	const getTodaysWorkLogs = () => {
		if (!isFinishWorkDateExpired) {
			dispatch(setTodayWorklogs());
		}
	};

	useEffect(() => {
		if (
			userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_READ,
			) ||
			userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_WRITE,
			)
		) {
			if (workLogListForClock?.data.length > 0) {
				dispatch(setTodayWorklogs(workLogListForClock));
			} else {
				getTodaysWorkLogs();
			}
		}
	}, [userData, workLogListForClock]);

	useEffectNotInitial(() => {
		if (
			userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_READ,
			) ||
			userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_WRITE,
			)
		) {
			getTodaysWorkLogs();
		}
	}, [reFetchFlag]);

	useEffect(() => {
		if (
			(userData?.employeeRolePrivileges?.includes(
				Privileges.ROLE_LOG_WORK_READ,
			) ||
				userData?.employeeRolePrivileges?.includes(
					Privileges.ROLE_LOG_WORK_WRITE,
				)) &&
			currentUtcTime &&
			isFirstInit.current
		) {
			isFirstInit.current = false;
			setShiftTime(userData.customer_setting.maxShiftHour);
		}
	}, [currentUtcTime]);

	const disableButtons = useMemo(() => {
		let disable = false;
		let eventName = '';
		const fulldayReport = todayWorkLogs.data.filter(
			workLog => workLog.eventId && workLog.fullDay,
		);

		if (fulldayReport.length) {
			disable = true;
			eventName = fulldayReport[0].eventName;
		}
		return { disable, eventName };
	}, [todayWorkLogs.data]);
	const disableEntryButton = useMemo(() => {
		//disable entry button if the user did not choose any project
		return projectsOptions.length && !defaultProjectOption.id;
	}, [projectsOptions, defaultProjectOption]);

	if (isFinishWorkDateExpired) return null;

	const alertIfMaxHoursReached = () => {
		if (totalReported >= userData?.customer_setting.maxShiftHour) {
			openAlertModal(
				`${i18nService.translate('general.maxShiftTimeError')} ${
					userData?.customer_setting.maxShiftHour
				} ${i18nService.translate('general.maxShiftTimeError2')}`,
				'attention',
			);
			return true;
		}
		return false;
	};

	const checkIfCanEntry = (option: { id: string }) => {
		const isIncludes = userData?.location.find(
			item =>
				item.projectId === option.id || (!!item.id && !item.projectId),
		);
		return !isIncludes;
	};

	const handleEntry = async e => {
		Platform.OS === 'web' && document?.activeElement?.blur?.();
		const projectId = !projectsOptions.length
			? null
			: selectedItem.id
			? selectedItem.id
			: defaultProjectOption.id;

		if (
			isWebOrMobileweb &&
			selectedItem.isProject &&
			!checkIfCanEntry({ id: projectId })
		) {
			dispatch(getCurrentDateTime());
			if (alertIfMaxHoursReached()) return;
			if (currentRunnigWorkLog || missingExitWorkLog.isMissing) {
				openAlertModal('general.alertCompleteEntry', 'attention');
			} else
				await cantReportModal(
					isMobile,
					userData?.location.find(pr => pr.projectId === projectId),
				);
		} else {
			dispatch(getCurrentDateTime());
			if (alertIfMaxHoursReached()) return;
			if (currentRunnigWorkLog || missingExitWorkLog.isMissing) {
				openAlertModal('general.alertCompleteEntry', 'attention');
			} else {
				try {
					const eventId = !eventsOptions.length
						? null
						: selectedItem.id && !selectedItem.isProject
						? selectedItem.id
						: null;

					let lat = null;
					let lon = null;
					if (
						platform === DeviceType.MOBILE &&
						selectedItem.isProject &&
						!checkIfCanEntry({ id: projectId })
					) {
						const location = await getLocationPermission();
						lat = location?.coords?.latitude || null;
						lon = location?.coords?.longitude || null;
						if (!location) {
							return;
						}
					}
					const data = {
						date: getCurrentLocalDate(),
						enterTime: moment(
							getState().dateTime.currentDateTime,
							'YYYY-MM-DDTHH:mm:ss',
						).format('YYYY-MM-DDTHH:mm:ss'),
						projectId: selectedItem.isProject ? projectId : null,
						eventId,
						mobile: !isWebOrMobileweb,
						lat,
						lon,
					};

					if (Platform.OS !== 'web') {
						const crashlytics = (
							await import('@react-native-firebase/crashlytics')
						).default;
						crashlytics().recordError(
							new Error(
								`entryClockWorkLog data: ${JSON.stringify(data)} `,
							),
						);
					}
					await httpService.api<any>({
						type: 'startClockWorklog',
						data,
					});

					dispatch(toggleReFetch());
					setInitTime({ h: 0, m: 0, s: 0 });
					snackbarService.show({
						message: defaultProjectOption.name
							? `${i18nService.translate(
									'general.WorkLogAddedSuccessfullyForProject',
							  )} ${defaultProjectOption.name} ${i18nService.translate(
									'general.Successfully',
							  )}`
							: i18nService.translate(
									'general.WorkLogAddedSuccessfully',
							  ),
						success: true,
					});
				} catch (error) {
					console.log(error);
					switch (error?.data?.error) {
						case 'LOCATION_PROJECT_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.project.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === projectId,
										),
								  );
							break;
						case 'LOCATION_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.location.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === projectId,
										),
								  );
							break;
						default:
							snackbarService.show({
								message: i18nService.translate(
									'general.serverError',
								),
								success: false,
							});
							break;
					}
				}
			}
		}
	};

	const performExit = async () => {
		Platform.OS === 'web' && document?.activeElement?.blur?.();
		const projectId = !projectsOptions.length
			? ''
			: selectedItem.id
			? selectedItem.id
			: defaultProjectOption.id;
		if (
			isWebOrMobileweb &&
			currentRunnigWorkLog?.projectId &&
			!checkIfCanEntry({ id: projectId })
		) {
			await cantReportModal(
				isMobile,
				userData?.location.find(pr => pr.projectId === projectId),
			);
		} else {
			dispatch(getCurrentDateTime());
			if (currentRunnigWorkLog || !canEdit) {
				const projectRunning = currentRunnigWorkLog?.projectName;
				const eventRunning = currentRunnigWorkLog?.eventName;

				let lat = null;
				let lon = null;

				if (
					platform === DeviceType.MOBILE &&
					currentRunnigWorkLog &&
					!currentRunnigWorkLog.eventId &&
					!checkIfCanEntry({ id: projectId })
				) {
					const location = await getLocationPermission();
					lat = location?.coords?.latitude || null;
					lon = location?.coords?.longitude || null;
					if (!location) {
						return;
					}
				}
				try {
					const data = {
						...(!canEdit && {
							date: getCurrentLocalDate(),
						}),
						exitTime: moment(
							getState().dateTime.currentDateTime,
							'YYYY-MM-DDTHH:mm',
						).format('YYYY-MM-DDTHH:mm'),
						mobile: !isWebOrMobileweb,
						lat,
						lon,
					};

					if (Platform.OS !== 'web') {
						const crashlytics = (
							await import('@react-native-firebase/crashlytics')
						).default;
						crashlytics().recordError(
							new Error(
								`exitClockWorkLog data: ${JSON.stringify(data)} `,
							),
						);
					}

					await httpService.api<any>({
						type: canEdit
							? 'exitClockWorklogById'
							: currentRunnigWorkLog
							? 'exitClockWorklogById'
							: 'exitClockWorkLog',
						data,
						params: {
							...(currentRunnigWorkLog
								? { workLogId: currentRunnigWorkLog?.workLogId }
								: {}),
						},
					});

					setInitTime(null); //should stop and remove the timer
					reportedExit.current = true;
					setSelectedItem({
						name: defaultProjectOption.name,
						isProject: true,
						id: defaultProjectOption.id,
						isFullDay: false,
					});
					dispatch(toggleReFetch());
					snackbarService.show({
						message: projectRunning
							? `${i18nService.translate(
									'general.WorkLogExitSuccessfullyForProject',
							  )} ${projectRunning} ${i18nService.translate(
									'general.Successfully',
							  )}`
							: eventRunning
							? `${i18nService.translate(
									'general.WorkLogExitSuccessfullyForEvent',
							  )} ${eventRunning} ${i18nService.translate(
									'general.Successfully',
							  )}`
							: i18nService.translate(
									'general.WorkLogClosedSuccessfully',
							  ),
						success: true,
					});
				} catch (error) {
					switch (error?.data?.error) {
						case 'LOCATION_PROJECT_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.project.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === projectId,
										),
								  );
							break;
						case 'LOCATION_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.location.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === projectId,
										),
								  );
							break;
						default:
							snackbarService.show({
								message: i18nService.translate(
									'general.serverError',
								),
								success: false,
							});
							break;
					}
				}
			} else {
				if (alertIfMaxHoursReached()) return;
				const sharedProps = {
					onCancel: (callback?: Function) => {
						callback?.();
					},
					onSubmit: async (callback: Function, date: string) => {
						dispatch(toggleReFetch());
						callback?.();
						setInitTime(null); //should stop and remove the timer
						setCompleteEntrySheetProps(null);
						getTodaysWorkLogs();
						snackbarService.show({
							message: `${i18nService.translate(
								'general.WorkLogAddedSuccessfullyForDate',
							)} ${date} `,
							success: true,
						});
					},
					projectsOptions,
					eventsOptions,
					shiftHours: shiftTime,
					totalReported,
					prevValues: {
						date: getCurrentLocalDate('DD/MM/YY'),
						projectId: defaultProjectOption.id,
						eventId: null,
					},
					submitBtnText: 'general.reportExit',
					cancelBtnText: 'general.cancel',
					checkIfCanEntry,
				};
				if (platform === DeviceType.MOBILE) {
					setCompleteEntrySheetProps(sharedProps);
				} else {
					await modalService.openModal(
						null,
						{ ...sharedProps, hideCloseBtn: true },
						(props: any) => <CompleteEntryContainer {...props} />,
					);
				}
			}
		}
	};

	const handleExit = async () => {
		modalService.openModal(
			null,
			{
				onCancel: (callback: Function) => {
					callback();
				},
				onSubmit: async (callback: Function) => {
					callback();
					await performExit();
				},
				submitBtnText: 'general.true',
				cancelBtnText: 'general.false',
				iconName: 'attention',
				hideCloseBtn: true,
			},
			({ onSubmit, onCancel, onNext, submitBtnText, cancelBtnText }: any) => (
				<View style={{width: isMobile ? '75%' : 450, padding: 32, backgroundColor: 'white', borderRadius: 8}}>
					 <View style={{ flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
						<I18n color='primaryDarkBlue' weight='bold' size={17} >
							modal.exitClock
						</I18n>
						<Pressable onPress={() => onCancel(onNext)} style={{padding: 4, marginLeft: -8}}>
							<Text style={{color: colors.darkGrey, lineHeight: 28, fontSize: 28,}}>×</Text>
						</Pressable>
					 </View>
					<I18n color='primaryDarkBlue' size={15} style={{ marginBottom: 40 }} >
						modal.subtitleClock
					</I18n>
					<View style={{ flexDirection: 'row'}}>
						<ActionButtonContainer
							overrideStyle={{width: 100, height: 40, backgroundColor: colors.titlePrimary, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 11, marginRight: 16 }}
							hoverStyle={{backgroundColor: colors.primaryDark}}
							onPress={() => onSubmit(onNext)}
						>
							<I18n size={16} weight='normal' color='white'>{submitBtnText}</I18n>
						</ActionButtonContainer>
						<ActionButtonContainer
							overrideStyle={{width: 100, height: 40, borderWidth: 1, borderColor: colors.titlePrimary, paddingHorizontal: 12, paddingVertical: 11 }}
							onPress={() => onCancel(onNext)}
						>
							<I18n size={16} weight='normal' color='primary'>{cancelBtnText}</I18n>
						</ActionButtonContainer>
					</View>
				</View>
			),
		);
	};

	const startWorkLog = async () => {
		const tempProjectId = selectedItem.id
			? selectedItem.id
			: defaultProjectOption.id;
		const eventId =
			selectedItem.id && !selectedItem.isProject ? selectedItem.id : null;
		let exitTime = null;
		let enterTime = moment(
			getState().dateTime.currentDateTime,
			'YYYY-MM-DDTHH:mm:ss',
		).format('YYYY-MM-DDTHH:mm:ss');
		const projectId = selectedItem.isProject ? tempProjectId : null;
		if (!selectedItem.isProject && selectedItem.isFullDay) {
			[exitTime, enterTime] = formatStandard();
		}

		let lat = null;
		let lon = null;
		if (
			platform === DeviceType.MOBILE &&
			selectedItem.isProject &&
			!checkIfCanEntry({ id: projectId })
		) {
			const location = await getLocationPermission();
			lat = location?.coords?.latitude || null;
			lon = location?.coords?.longitude || null;
			if (!location) {
				return;
			}
		}
		const data = {
			date: getCurrentLocalDate(),
			enterTime,
			exitTime,
			projectId,
			eventId,
			fullDay: projectId ? false : selectedItem.isFullDay,
			mobile: !isWebOrMobileweb,
			lat,
			lon,
		};

		if (Platform.OS !== 'web') {
			const crashlytics = (
				await import('@react-native-firebase/crashlytics')
			).default;
			crashlytics().recordError(
				new Error(`startWorkLog data: ${JSON.stringify(data)} `),
			);
		}

		try {
			const res = await httpService.api<any>({
				type: 'startClockWorklog',
				data,
			});
		} catch (error) {
			switch (error.data.error) {
				case 'LOCATION_PROJECT_ERROR':
					platform === DeviceType.MOBILE
						? openAlertModal(
								'workLogError.project.phone',
								'attention',
								'general.close',
						  )
						: await cantReportModal(
								isMobile,
								userData?.location.find(
									pr => pr.projectId === projectId,
								),
						  );
					break;
				case 'LOCATION_ERROR':
					platform === DeviceType.MOBILE
						? openAlertModal(
								'workLogError.location.phone',
								'attention',
								'general.close',
						  )
						: await cantReportModal(
								isMobile,
								userData?.location.find(
									pr => pr.projectId === projectId,
								),
						  );
					break;
				case 'PARTIAL_ERROR':
					openAlertModal(
						'general.alertCompleteEntry',
						'attention',
						'',
					);
					break;
				default:
					openAlertModal(
						'general.WorkLogAddingFailed',
						'attention',
						'',
					);
					break;
			}
			return;
		}

		dispatch(toggleReFetch());
		getTodaysWorkLogs();
		setInitTime({ h: 0, m: 0, s: 0 });
		if (!selectedItem.isProject && selectedItem.isFullDay) {
			setSelectedItem({
				name: null,
				isProject: true,
				id: null,
				isFullDay: true,
			});
		}
	};

	const formatStandard = () => {
		let enterTime = '08:00:00';
		let exitTime = '08:00:00';
		if (standardHours) {
			if (
				standardHours.from === '00:00:00' &&
				standardHours.to === '00:00:00'
			) {
				const [hours, minutes, seconds] = standardHours.fixHours
					.toString()
					.split(':');
				exitTime = moment('08:00:00', 'HH:mm:ss')
					.add(toNumber(hours), 'h')
					.format('HH:mm:ss');
				exitTime = moment(exitTime, 'HH:mm:ss')
					.add(toNumber(minutes), 'm')
					.format('HH:mm:ss')
					.toString();
			} else {
				enterTime = standardHours.from;
				exitTime = standardHours.to;
			}
		}
		//convert to utc before return
		exitTime = moment(exitTime, 'HH:mm:ss')
			.utc()
			.format('YYYY-MM-DDTHH:mm:ss');
		enterTime = moment(enterTime, 'HH:mm:ss')
			.utc()
			.format('YYYY-MM-DDTHH:mm:ss');
		return [exitTime, enterTime];
	};

	const handleProjectOrEventSelect = async (
		option: Option,
		isEventSelect: boolean,
		isFullDay: boolean,
	) => {
		if (isWebOrMobileweb && !isEventSelect && !checkIfCanEntry(option)) {
			await cantReportModal(
				isMobile,
				userData?.location.find(pr => pr.projectId === option.id),
			);
		} else {
			dispatch(getCurrentDateTime());
			if (alertIfMaxHoursReached()) return;
			if (currentRunnigWorkLog) {
				const runnigProject = currentRunnigWorkLog.projectName;
				const runnigEvent = currentRunnigWorkLog.eventName;
				try {
					let lat = null;
					let lon = null;
					if (
						platform === DeviceType.MOBILE &&
						selectedItem.isProject &&
						!checkIfCanEntry({
							id: currentRunnigWorkLog.projectId,
						})
					) {
						const location = await getLocationPermission();
						lat = location?.coords?.latitude || null;
						lon = location?.coords?.longitude || null;
						if (!location) {
							return;
						}
					}

					const data = {
						exitTime: moment(
							getState().dateTime.currentDateTime,
							'YYYY-MM-DDTHH:mm',
						).format('YYYY-MM-DDTHH:mm'),
						mobile: !isWebOrMobileweb,
						lat,
						lon,
					};

					if (Platform.OS !== 'web') {
						const crashlytics = (
							await import(
								'@react-native-firebase/crashlytics'
							)
						).default;
						crashlytics().recordError(
							new Error(
								`selectProjectOrEvent data: ${JSON.stringify(
									data,
								)} `,
							),
						);
					}

					await httpService.api<any>({
						type: 'exitClockWorklogById',
						data,
						params: {
							workLogId: currentRunnigWorkLog.workLogId,
						},
					});

					setSelectedItem({
						name: option.name,
						isProject: !isEventSelect,
						id: option.id,
						isFullDay,
					});
					if (!isEventSelect)
						setDefaultProjectOption({ ...option });
					snackbarService.show({
						message: runnigProject
							? `${i18nService.translate(
									'general.WorkLogExitSuccessfullyForProject',
							  )} ${runnigProject} ${i18nService.translate(
									'general.Successfully',
							  )}`
							: runnigEvent
							? `${i18nService.translate(
									'general.WorkLogExitSuccessfullyForEvent',
							  )} ${runnigEvent} ${i18nService.translate(
									'general.Successfully',
							  )}`
							: i18nService.translate(
									'general.WorkLogClosedSuccessfully',
							  ),
						success: true,
					});
				} catch (error) {
					switch (error.data.error) {
						case 'LOCATION_PROJECT_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.project.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === option.id,
										),
								  );
							break;
						case 'LOCATION_ERROR':
							platform === DeviceType.MOBILE
								? snackbarService.show({
										message: i18nService.translate(
											'workLogError.location.phone',
										),
										success: false,
								  })
								: await cantReportModal(
										isMobile,
										userData?.location.find(
											pr => pr.projectId === option.id,
										),
								  );
							break;
						default:
							break;
					}
				}
			} else {
				setSelectedItem({
					name: option.name,
					isProject: !isEventSelect,
					id: option.id,
					isFullDay,
				});
				if (!isEventSelect) setDefaultProjectOption({ ...option });
				snackbarService.show({
					message: `${i18nService.translate(
						isEventSelect
							? 'general.youAreNowReportingEvent'
							: 'general.youAreNowReportingProject',
					)} ${option.name}`,
					success: true,
				});
			}
		}
	};

	const handleDelete = async (id: string) => {
		if (workLogListForClock?.data) {
			workLogListForClock.data = [];
		}
		onDelete(id, () => {
			dispatch(toggleReFetch());
		});
	};

	const handleEdit = async (id, workLog) => {
		const fullName = `${userData?.first_name} ${userData?.last_name}`;
		let logValues = {};
		if (id && !workLog) {
			logValues = await httpService.api<any>({
				type: 'workLogByIdEmployee',
				params: { workLogId: id },
			});
		}
		const editSharedProps = {
			onCancel: (callback: Function) => {
				callback();
			},
			onSubmit: async (callback: Function) => {
				dispatch(toggleReFetch());
				callback();
			},
			submitBtnText: 'general.accept',
			route: route,
			cancelBtnText: 'general.cancel',
			isEdit: true,
			openEditModal: handleEdit,
			workLogId: id,
			isManager: false,
			hideName: true,
			hideCloseBtn: true,
			prevValues: {
				...(workLog || logValues),
				originalExitEntry: {
					entryTime:
						id && !workLog
							? logValues.entryTime
							: workLog.enterTime,
					exitTime:
						id && !workLog
							? logValues.exitTime
							: workLog.exitTime,
				},
				entryTime: convertFromUtc(
					workLog ? workLog.enterTime : logValues.entryTime,
					true,
				),
				exitTime: convertFromUtc(
					workLog ? workLog.exitTime : logValues.exitTime,
					true,
				),
				employee: {
					id: userData?.id,
					name: fullName,
				},
				fullName,
				date: getCurrentLocalDate(),
			},
			totalReported,
			shiftHours: userData?.customer_setting.maxShiftHour,
		};
		if (platform === DeviceType.MOBILE) {
			setAddRequestSheetProps(editSharedProps);
		} else {
			await modalService.openModal(
				null,
				editSharedProps,
				(props: any) => <AddRequest {...props} />,
			);
		}
	};

	const handleReachedMax = () => {
		setCurrentRunningWorkLog(null);
		setInitTime(null);
		dispatch(toggleReFetch());
	};

	const handleAddNewLog = async eventId => {
		const addNewSharedProps = {
			onCancel: (callback: Function) => {
				callback();
			},
			onSubmit: async (callback: Function) => {
				dispatch(toggleReFetch());
				callback();
			},
			shiftHours: userData?.customer_setting.maxShiftHour,
			submitBtnText: 'general.accept',
			route: { name: 'calendar' },
			cancelBtnText: 'general.cancel',
			isEdit: false,
			isManager: false,
			hideName: true,
			addNew: true,
			hideProjects: true,
			hideCloseBtn: true,
			prevValues: {
				date: getCurrentLocalDate(),
				employeeId: userData?.id,
				fullName: `${userData?.first_name} ${userData?.last_name}`,
				eventId,
				fullDay: true,
			},
			fullEvent: true,
			selectedDateForEvent: getCurrentLocalDate(),
		};
		if (platform === DeviceType.MOBILE) {
			setAddRequestSheetProps(addNewSharedProps);
		} else {
			await modalService.openModal(
				null,
				addNewSharedProps,
				(props: any) => <AddRequest {...props} />,
			);
		}
	};

	const openProjectPicker = async () => {
		try {
			await modalService.openModal(
				null,
				{
					onCancel: (callback?: Function) => {
						callback?.();
					},
					onSubmit: async (
						callback: Function,
						selectedOption: any,
						isEventSelect: boolean,
						isFullDaySelected: boolean,
					) => {
						try {
							handleProjectOrEventSelect(
								selectedOption,
								isEventSelect,
								isFullDaySelected,
							);
							callback();
						} catch (error) {
							console.log(error);
						}
					},
				},
				(props: any) => (
					<EventsAndProjectsSelectComponent
						{...props}
						currentRunnigWorkLog={currentRunnigWorkLog}
						canReportFullDay={canReportFullDay}
						defaultOption={defaultProjectOption}
						options={projectsOptions}
						isEventSelect={false}
						isModal={true}
						closed={() => {}}
					/>
				),
			);
		} catch (e) {
			console.log(e);
		}
	};
	return (
		<>
		<Component
			compact={compact}
			selectedItemType={selectedItemType}
			handleOpenOptions={handleOpenOptions}
			report101warning={report101warning}
			disableEntryButton={disableEntryButton}
			disableButtons={disableButtons}
			canReportFullDay={canReportFullDay}
			initialTime={initTime}
			projectsOptions={projectsOptions}
			eventsOptions={eventsOptions}
			eventPlaceholdar={
				currentRunnigWorkLog?.eventId
					? currentRunnigWorkLog?.eventName
					: 'menu.chooseEvent'
			}
			label={
				currentRunnigWorkLog?.projectId
					? `${i18nService.translate(
							'general.youAreNowReportingProject',
					  )} ${i18nService.translate(
							currentRunnigWorkLog?.projectName,
					  )}`
					: currentRunnigWorkLog?.eventId
					? `${i18nService.translate(
							'general.youAreNowReportingEvent',
					  )} ${i18nService.translate(
							currentRunnigWorkLog?.eventName,
					  )}`
					: null
			}
			records={todayWorkLogs.data}
			onSelectProjectOrEvent={handleProjectOrEventSelect}
			totalReported={totalReported}
			activeDayTotalReported={activeDayTotalReported}
			standardHours={standardHours}
			onPressEntry={handleEntry}
			onPressExit={handleExit}
			onPressReportExit={performExit}
			shiftTime={shiftTime}
			defaultProjectOption={defaultProjectOption}
			selectedItem={selectedItem}
			defaultEventOption={defaultEventOption}
			reachedMax={handleReachedMax}
			onEdit={handleEdit}
			onDelete={handleDelete}
			currentRunnigWorkLog={currentRunnigWorkLog}
			handleAddNewLog={handleAddNewLog}
			openProjectPicker={openProjectPicker}
			splitCards={splitCards}
			canEdit={canEdit}
		/>
		{isMobile && (
			<CompleteEntryBottomSheet
				visible={!!completeEntrySheetProps}
				sheetProps={completeEntrySheetProps}
				onClose={() => setCompleteEntrySheetProps(null)}
			/>
		)}
		{isMobile && (
			<AddRequestBottomSheet
				visible={!!addRequestSheetProps}
				sheetProps={addRequestSheetProps}
				onClose={() => setAddRequestSheetProps(null)}
			/>
		)}
		</>
	);
};

export default ClockReportContainer;
